import { useEffect } from "react";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";
import { Logo } from "@allaria-tech/matter/Logo";
import { Typography } from "@allaria-tech/matter/Typography";

import Layout from "../Layout/Layout";
import FalFundCard from "./FalFundCard";
import FalFundConfirmModal from "./FalFundConfirmModal";
import PartnerConsentFeedback from "../PartnerConsentPage/PartnerConsentFeedback";
import { useAppDispatch } from "../../app/hooks";
import { onUpdateTheme } from "../../reducers/activeTemplateReducer";
import useFalFundSelection from "../../hooks/useFalFundSelection";
import useWindowWidth from "../../hooks/useWindowWidth";
import { falFundSelectionCopy as copy } from "../../i18n/falFundSelection";
import {
  PartnerConsentFlow,
  getPartnerConsentFlow,
} from "../../utils/partnerConsent";

import styles from "./FalFundSelectionPage.module.scss";

const MOBILE_BREAKPOINT = 768;

// Only for the feedback screens' Allaria Fondos lockup.
const falFlow = getPartnerConsentFlow("allaria-fal") as PartnerConsentFlow;

const FalFundSelectionPage = () => {
  const dispatch = useAppDispatch();
  const isMobile = useWindowWidth().width < MOBILE_BREAKPOINT;
  const {
    screen,
    funds,
    holder,
    pendingFund,
    chosenFund,
    chosenAt,
    isSubmitting,
    choose,
    cancel,
    confirm,
    retry,
  } = useFalFundSelection();

  useEffect(() => {
    dispatch(onUpdateTheme("ALLARIA"));
  }, [dispatch]);

  const { feedback } = copy;
  const { panel, header, titles, cards, disclaimer } = styles;

  const renderScreen = () => {
    switch (screen) {
      case "SUCCESS":
        return (
          <PartnerConsentFeedback
            flow={falFlow}
            isMobile={isMobile}
            type="SUCCESS"
            title={feedback.success.title}
            description={feedback.success.description(chosenFund, holder)}
          />
        );

      case "ALREADY_CHOSEN":
        return (
          <PartnerConsentFeedback
            flow={falFlow}
            isMobile={isMobile}
            type="SUCCESS"
            title={feedback.alreadyChosen.title}
            description={feedback.alreadyChosen.description(
              chosenFund,
              chosenAt,
            )}
          />
        );

      case "NOT_REGISTERED":
        return (
          <PartnerConsentFeedback
            flow={falFlow}
            isMobile={isMobile}
            type="ERROR"
            title={feedback.notRegistered.title}
            description={feedback.notRegistered.description}
          />
        );

      case "INVALID_LINK":
        return (
          <PartnerConsentFeedback
            flow={falFlow}
            isMobile={isMobile}
            type="ERROR"
            title={feedback.invalidLink.title}
            description={feedback.invalidLink.description}
          />
        );

      case "ERROR":
        return (
          <PartnerConsentFeedback
            flow={falFlow}
            isMobile={isMobile}
            type="ERROR"
            title={feedback.genericError.title}
            description={feedback.genericError.description}
            buttonText={feedback.retry}
            onButtonClick={retry}
          />
        );

      default:
        if (!funds) return null;
        return (
          <div className={`${panel} scrollbar`}>
            <div className={header}>
              <Logo product="fondos" />
              <div className={titles}>
                <Typography type="HEADING" size="L" tag="h1">
                  {copy.title}
                </Typography>
                <Typography
                  type="BODY"
                  size="M"
                  tag="p"
                  color="var(--typography-heading-color-emphasis)"
                >
                  {copy.subtitle(holder)}
                </Typography>
              </div>
            </div>

            <div className={cards}>
              {funds.fondos.map((fund) => (
                <FalFundCard
                  key={fund.id}
                  fund={fund}
                  ratesDate={funds.fechaCifras}
                  onChoose={() => choose(fund)}
                />
              ))}
            </div>

            <Typography
              type="DETAIL"
              size="S"
              tag="p"
              className={disclaimer}
              color="var(--typography-body-color-soft)"
            >
              {copy.disclaimer(funds.fechaCifras)}
            </Typography>

            <FalFundConfirmModal
              fund={pendingFund}
              holder={holder}
              isSubmitting={isSubmitting}
              onCancel={cancel}
              onConfirm={confirm}
            />
          </div>
        );
    }
  };

  if (screen === "LOADING")
    return (
      <Layout
        hideHeader
        hideMobileHeader
        hideStepper
        hideSupportButton
        isLoading
      >
        <LoadingCircle />
      </Layout>
    );

  return (
    <Layout hideHeader hideMobileHeader hideStepper hideSupportButton>
      {renderScreen()}
    </Layout>
  );
};

export default FalFundSelectionPage;
